import {assert} from 'Asserts';
import {
  CreateMixtureGroupExtras,
  MixtureGroupMetadata,
} from 'notebook/integrations/registry/MixtureGroupConstants';
import {isMixtureGroup} from 'notebook/integrations/registry/MixtureGroupConstants';
import {MixtureNoteLinkedGroupAdapter} from 'notebook/integrations/registry/MixtureNoteLinkedGroupAdapter';
import makeSimpleGroupActionFunction from 'notebook/notes/groups/action-functions/makeSimpleGroupActionFunction';
import {getNoteLinkedGroupAdapters} from 'notebook/notes/groups/NoteLinkedGroupAdapters';
import {NoteLinkedGroupKind} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import toColumnLabel from 'notebook/structured-tables/columns/toColumnLabel';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import makeTableCSV from 'notebook/tables/export/makeTableCSV';
import {initiateTextDownload} from 'util/TextToBlobUtil';

async function initiateCSVDownload(
  structuredTable: UnknownStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>
): Promise<void> {
  const structuredTableAdapter = getStructuredTableAdapters()[structuredTable.kind];
  const tableCells = structuredTableAdapter.toTable(structuredTable).table;

  initiateTextDownload(
    await makeTableCSV({
      table: tableCells,
      computedTable,
      headerRow: structuredTable.columns.map(
        (structuredTableColumn) =>
          toColumnLabel({structuredTableColumn, schemas: structuredTable.schemas}) || 'unknown'
      ),
      includeCellValueModifiers: false,
    }),
    `${structuredTable.name}.csv`
  );
}

export const downloadMixtureCSVActionFunction = makeSimpleGroupActionFunction<
  MixtureGroupMetadata,
  CreateMixtureGroupExtras
>({
  label: 'Download mixture as CSV',
  icon: 'download',

  onInvoke: ({noteLinkedGroup, evaluatedTableMap}) => {
    assert(isMixtureGroup(noteLinkedGroup), 'Download csv for mixture can only be used in a mixture group');
    const adapter = getNoteLinkedGroupAdapters()[
      NoteLinkedGroupKind.MIXTURE_GROUP
    ] as MixtureNoteLinkedGroupAdapter;
    const structuredTable = adapter.getMixtureMetadataStructuredTable(noteLinkedGroup);
    const computedTable = evaluatedTableMap[structuredTable.name];
    assert(computedTable != null, 'Unable to find evaluated table');
    initiateCSVDownload(structuredTable, computedTable);
  },
});

export const downloadIngredientsCSVActionFunction = makeSimpleGroupActionFunction<
  MixtureGroupMetadata,
  CreateMixtureGroupExtras
>({
  label: 'Download ingredients as CSV',
  icon: 'download',

  onInvoke: ({noteLinkedGroup, evaluatedTableMap}) => {
    assert(isMixtureGroup(noteLinkedGroup), 'Download csv for mixture can only be used in a mixture group');
    const adapter = getNoteLinkedGroupAdapters()[
      NoteLinkedGroupKind.MIXTURE_GROUP
    ] as MixtureNoteLinkedGroupAdapter;
    const structuredTable = adapter.getMixtureIngredientsStructuredTable(noteLinkedGroup);
    const computedTable = evaluatedTableMap[structuredTable.name];
    assert(computedTable != null, 'Unable to find evaluated table');
    initiateCSVDownload(structuredTable, computedTable);
  },

  getDisabled: ({noteLinkedGroup}) => {
    const adapter = getNoteLinkedGroupAdapters()[
      NoteLinkedGroupKind.MIXTURE_GROUP
    ] as MixtureNoteLinkedGroupAdapter;
    const structuredTable = adapter.getMixtureIngredientsStructuredTable(noteLinkedGroup);
    return structuredTable.rows.length === 0;
  },
});
